"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import ReCAPTCHA from "react-google-recaptcha";
import { motion, useReducedMotion } from "motion/react";
import {
  ArrowRight,
  ArrowUpRight,
  BadgeCheck,
  Calendar,
  Check,
  ChevronRight,
  Clock,
  Loader2,
  Menu,
  Quote,
  Shield,
  Sparkles,
  Users,
  Video,
  X,
} from "lucide-react";
import {
  trackEvent,
  trackMetaStandardEvent,
  trackGoogleAdsLead,
} from "@/lib/analytics";

// ──────────────────────────────────────────────────────────────────────────
// EVENT CONFIG — single source of truth. Edit when the date/speaker are set.
// Use a full ISO string with the Romania offset for `startsAt`.
// ──────────────────────────────────────────────────────────────────────────
const WEBINAR = {
  startsAt: "2026-07-09T18:00:00+03:00",
  dateLabel: "Joi, 9 iulie",
  timeLabel: "18:00",
  durationLabel: "~90 de minute",
  format: "Online, pe Google Meet",
  price: "Gratuit",
  speakerName: "Paula",
  // PLACEHOLDER — fill the speaker's real details; these drive the trust signals.
  speakerFullName: "Paula Pricope",
  speakerRole: "Cofondatoare Mindtale · psihoterapeut acreditat COPSI",
  speakerYears: 10,
  speakerFocus: "stres și burnout",
  // Relative, and without the `www.` host: the canonical host is the apex, so
  // an absolute www URL sent both visitors and Googlebot through a redirect.
  speakerProfileUrl: "/terapeuti/paula-pricope",
  burnoutTestUrl: "/teste/burnout",
  // Social proof: set to a real number once you have signups (e.g. 120) to show
  // "Alătură-te celor 120+ înscriși"; keep null to fall back to the authority line.
  registrantCount: null as number | null,
};

// Google Calendar "add event" link, built from the webinar date (+1h end).
// Runs client-side only (used in the post-signup success card).
function googleCalUrl(): string {
  const start = new Date(WEBINAR.startsAt);
  const end = new Date(start.getTime() + 90 * 60 * 1000);
  const fmt = (d: Date) =>
    d.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
  const text = encodeURIComponent("Webinar Mindtale: Burnout");
  const details = encodeURIComponent(
    "Webinar gratuit despre burnout. Link-ul de acces ajunge pe email.",
  );
  return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${text}&dates=${fmt(start)}/${fmt(end)}&details=${details}`;
}

// reCAPTCHA only runs when the public site key is configured. In local dev
// without the key we skip the widget (and the server skips verification too),
// so the page never crashes with "Missing required parameters: sitekey".
const RECAPTCHA_SITE_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;

const GDPR_URL = "https://app.mindtale.ro/termeni-si-conditii?doc=gdpr";

/** Fade-up reveal on scroll. Respects reduced-motion; 800ms mount fallback so
 *  content never stays invisible on flaky mobile WebViews. */
function Reveal({
  children,
  delay = 0,
  className,
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) {
  const reduce = useReducedMotion();
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const t = window.setTimeout(() => setMounted(true), 800);
    return () => window.clearTimeout(t);
  }, []);

  if (reduce || mounted) {
    return (
      <motion.div
        className={className}
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay, ease: [0.2, 0.65, 0.35, 1] }}
      >
        {children}
      </motion.div>
    );
  }

  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.1, margin: "0px 0px -60px 0px" }}
      transition={{ duration: 0.5, delay, ease: [0.2, 0.65, 0.35, 1] }}
    >
      {children}
    </motion.div>
  );
}

function scrollToForm(location: string) {
  trackEvent("webinar_lp_cta_click", { location });
  document
    .getElementById("inscriere")
    ?.scrollIntoView({ behavior: "smooth", block: "start" });
}

// ──────────────────────────────────────────────────────────────────────────
// Registration logic. Collects nume, prenume, email, telefon + a required GDPR
// consent. reCAPTCHA + honeypot guard against spam.
// ──────────────────────────────────────────────────────────────────────────
function useWebinarForm(location: string) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [gdpr, setGdpr] = useState(false);
  const [website, setWebsite] = useState(""); // honeypot
  const [status, setStatus] = useState<"idle" | "loading" | "done" | "error">(
    "idle",
  );
  const [error, setError] = useState("");
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const phoneOk = /^[0-9 +().-]{9,}$/.test(phone.trim());

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    if (website) return; // bot caught by honeypot
    if (!firstName.trim() || !lastName.trim()) {
      setError("Completează numele și prenumele.");
      return;
    }
    if (!emailOk) {
      setError("Verifică adresa de email.");
      return;
    }
    if (!phoneOk) {
      setError("Verifică numărul de telefon.");
      return;
    }
    if (!gdpr) {
      setError("Trebuie să accepți politica de confidențialitate.");
      return;
    }

    setStatus("loading");
    try {
      // Invisible reCAPTCHA — same flow as the contact form. Skipped in dev
      // when no site key is configured.
      let token: string | undefined;
      if (RECAPTCHA_SITE_KEY) {
        token = (await recaptchaRef.current?.executeAsync()) ?? undefined;
        recaptchaRef.current?.reset();
        if (!token) {
          setError("Verificarea anti-spam a eșuat. Reîncearcă.");
          setStatus("idle");
          return;
        }
      }

      const res = await fetch("/api/webinar-register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: email.trim(),
          phone: phone.trim(),
          source: "webinar-burnout",
          token,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.error || "Ceva n-a mers. Mai încearcă o dată.");
        setStatus("idle");
        return;
      }

      // Conversion tracking — one Lead to GA4 + Meta + Google Ads, deduped by a
      // shared event id, with first-party email/name for advanced matching +
      // enhanced conversions.
      const leadId =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.round(Math.random() * 1e9)}`;
      const em = email.trim();
      trackEvent("webinar_register_submit", { webinar: "burnout", location });
      trackMetaStandardEvent(
        "Lead",
        { content_name: "webinar-burnout" },
        leadId,
        {
          email: em,
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          phone: phone.trim(),
        },
      );
      trackGoogleAdsLead("webinar", { userEmail: em, transactionId: leadId });

      setStatus("done");
    } catch {
      setError("Ceva n-a mers. Mai încearcă o dată în câteva secunde.");
      setStatus("idle");
    }
  }

  return {
    firstName, setFirstName, lastName, setLastName, email, setEmail,
    phone, setPhone, gdpr, setGdpr, website, setWebsite,
    status, error, submit, recaptchaRef,
  };
}

// Post-signup confirmation, including an "add to calendar" link to cut no-shows.
function SuccessCard({ email, compact = false }: { email: string; compact?: boolean }) {
  return (
    <div
      className={
        compact
          ? "rounded-2xl border border-[#2C969B]/20 bg-white p-5 text-center shadow-[0_8px_28px_rgba(44,150,155,0.10)]"
          : "mx-auto mt-8 max-w-md rounded-3xl border border-[#2C969B]/20 bg-white p-8 text-center shadow-[0_8px_28px_rgba(44,150,155,0.10)]"
      }
    >
      <span className="mx-auto flex size-12 items-center justify-center rounded-full bg-[#2C969B]/12 ring-1 ring-[#2C969B]/25">
        <Check className="size-6 text-[#0F6B70]" />
      </span>
      <h3 className="mt-3 text-[17px] font-semibold text-[#264653]">
        Gata, ești înscris!
      </h3>
      <p className="mx-auto mt-1.5 max-w-xs text-[13px] leading-relaxed text-[#264653]/70">
        Ți-am trimis confirmarea pe {email}. Ne vedem pe {WEBINAR.dateLabel}.
      </p>
      <a
        href={googleCalUrl()}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => trackEvent("webinar_add_to_calendar", { webinar: "burnout" })}
        className="mt-4 inline-flex h-10 items-center gap-2 rounded-full border border-[#2C969B]/30 bg-white px-4 text-[13px] font-semibold text-[#0F6B70] transition hover:bg-[#2C969B]/8"
      >
        <Calendar className="size-4" />
        Adaugă în calendar
      </a>
    </div>
  );
}

// Social proof near the CTA. Shows the registrant count once it's real,
// otherwise an authority line built from the speaker's credentials.
function SocialProof({ className = "" }: { className?: string }) {
  const count = WEBINAR.registrantCount;
  if (count && count > 0) {
    return (
      <p className={`inline-flex items-start gap-1.5 text-left text-[13px] font-medium text-[#264653]/75 ${className}`}>
        <Users className="mt-[2px] size-4 shrink-0 text-[#2C969B]" />
        Alătură-te celor {count}+ înscriși
      </p>
    );
  }
  return (
    <p className={`inline-flex items-start gap-1.5 text-left text-[13px] font-medium text-[#264653]/75 ${className}`}>
      <BadgeCheck className="mt-[2px] size-4 shrink-0 text-[#2C969B]" />
      Susținut de un psihoterapeut cu {WEBINAR.speakerYears} ani de practică în{" "}
      {WEBINAR.speakerFocus}
    </p>
  );
}

// Gentle, human urgency (no digital countdown). Computed after mount to avoid
// an SSR/CSR hydration mismatch on the relative day count.
function UrgencyLine() {
  const [label, setLabel] = useState<string | null>(null);
  useEffect(() => {
    const ms = new Date(WEBINAR.startsAt).getTime() - Date.now();
    if (Number.isNaN(ms)) return;
    const days = Math.ceil(ms / 86_400_000);
    // Scarcity only when it's actually near — "locurile se ocupă" a month out
    // reads as fake and hurts trust.
    if (days > 1) setLabel(`Începe în ${days} zile · locuri limitate`);
    else if (days === 1) setLabel("Începe mâine · locuri limitate");
    else if (days === 0) setLabel("Începe azi · ultimele locuri");
  }, []);
  if (!label) return null;
  return (
    <span className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-[#DC7050]/10 px-3 py-1 text-[12px] font-semibold text-[#C9542F]">
      <Clock className="size-3.5" />
      {label}
    </span>
  );
}

// Full registration form for the #inscriere section.
function WebinarForm({ location }: { location: string }) {
  const f = useWebinarForm(location);

  if (f.status === "done") return <SuccessCard email={f.email} />;

  const inputCls =
    "mt-1.5 h-12 w-full rounded-xl border border-[#264653]/15 bg-[#FAFAFA] px-4 text-[15px] text-[#264653] placeholder:text-[#264653]/40 outline-none transition focus:border-[#2C969B] focus:bg-white focus:ring-2 focus:ring-[#2C969B]/25";

  return (
    <form
      onSubmit={f.submit}
      className="mx-auto mt-8 max-w-md rounded-3xl border border-[#264653]/10 bg-white p-6 text-left shadow-[0_8px_28px_rgba(38,70,83,0.06)] sm:p-8"
    >
      {/* Honeypot — hidden from humans */}
      <input
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        value={f.website}
        onChange={(e) => f.setWebsite(e.target.value)}
        className="absolute left-[-9999px] h-0 w-0 opacity-0"
        aria-hidden
      />
      {RECAPTCHA_SITE_KEY && (
        <ReCAPTCHA
          ref={f.recaptchaRef}
          size="invisible"
          sitekey={RECAPTCHA_SITE_KEY}
        />
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block">
          <span className="text-[13px] font-medium text-[#264653]/80">Prenume</span>
          <input
            type="text"
            value={f.firstName}
            onChange={(e) => f.setFirstName(e.target.value)}
            placeholder="Prenumele tău"
            autoComplete="given-name"
            className={inputCls}
          />
        </label>
        <label className="block">
          <span className="text-[13px] font-medium text-[#264653]/80">Nume</span>
          <input
            type="text"
            value={f.lastName}
            onChange={(e) => f.setLastName(e.target.value)}
            placeholder="Numele tău"
            autoComplete="family-name"
            className={inputCls}
          />
        </label>
      </div>

      <label className="mt-4 block">
        <span className="text-[13px] font-medium text-[#264653]/80">Email</span>
        <input
          type="email"
          value={f.email}
          onChange={(e) => f.setEmail(e.target.value)}
          placeholder="tu@exemplu.ro"
          autoComplete="email"
          className={inputCls}
        />
      </label>

      <label className="mt-4 block">
        <span className="text-[13px] font-medium text-[#264653]/80">Telefon</span>
        <input
          type="tel"
          value={f.phone}
          onChange={(e) => f.setPhone(e.target.value)}
          placeholder="07xx xxx xxx"
          autoComplete="tel"
          className={inputCls}
        />
      </label>

      <label className="mt-4 flex items-start gap-2.5">
        <input
          type="checkbox"
          checked={f.gdpr}
          onChange={(e) => f.setGdpr(e.target.checked)}
          className="mt-0.5 size-4 shrink-0 rounded border-[#264653]/30 text-[#2C969B] accent-[#2C969B] focus:ring-2 focus:ring-[#2C969B]/30"
        />
        <span className="text-[12px] leading-relaxed text-[#264653]/65">
          Sunt de acord cu prelucrarea datelor mele pentru acest webinar, conform{" "}
          <a
            href={GDPR_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-[#264653]"
          >
            politicii de confidențialitate (GDPR)
          </a>
          .
        </span>
      </label>

      {f.error && (
        <p className="mt-3 text-[13px] font-medium text-[#C9542F]">{f.error}</p>
      )}

      <button
        type="submit"
        disabled={f.status === "loading"}
        className="mt-5 inline-flex h-12 w-full items-center justify-center gap-2 rounded-full bg-[#2C969B] text-[15px] font-semibold text-white shadow-[0_8px_22px_rgba(44,150,155,0.32)] transition hover:bg-[#22797e] disabled:opacity-70"
      >
        {f.status === "loading" ? (
          <>
            <Loader2 className="size-4 animate-spin" /> Se trimite…
          </>
        ) : (
          <>
            Rezervă locul <ArrowRight className="size-4" />
          </>
        )}
      </button>
    </form>
  );
}

export default function WebinarBurnoutClient({
  ended = false,
}: {
  ended?: boolean;
}) {
  useEffect(() => {
    trackEvent("webinar_lp_view", { webinar: "burnout", ended });
  }, [ended]);

  // The event is over. The link keeps circulating in old posts, emails and ads,
  // so the page stays up but stops selling a date that has passed.
  if (ended) return <EndedPage />;

  return (
    <div className="min-h-screen bg-white pb-20 sm:pb-0">
      <MinimalHeader />

      <Hero />

      <TrustBand />

      <Reveal>
        <EmpathySection />
      </Reveal>

      <Reveal>
        <WhatYouLearn />
      </Reveal>

      <MidCTA />

      <Reveal>
        <Speaker />
      </Reveal>

      <Reveal>
        <Agenda />
      </Reveal>

      <Registration />

      <Reveal>
        <FAQ />
      </Reveal>

      <Reveal>
        <FinalCTA />
      </Reveal>

      <MinimalFooter />

      <StickyMobileCTA />
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Minimal header — same warm glass bar as the rest of the site. Single primary
// action: reserve a seat. No nav distractions (CRO for paid/social traffic).
// ──────────────────────────────────────────────────────────────────────────
const MENU_LINKS = [
  { href: "/", label: "Acasă" },
  { href: "/burnout", label: "Despre burnout" },
  { href: "/terapeuti", label: "Terapeuți" },
  { href: "/blog", label: "Blog" },
  { href: "/despre-noi", label: "Despre noi" },
];

function MinimalHeader() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  return (
    <header className="sticky top-0 z-40 px-3 pt-3 sm:px-4">
      <div className="mx-auto flex h-14 max-w-5xl items-center justify-between rounded-2xl border border-[#264653]/8 bg-white/85 px-3 shadow-[0_4px_28px_rgba(38,70,83,0.08)] backdrop-blur-xl sm:h-16 sm:px-4">
        <Link href="/" aria-label="Mindtale" className="flex items-center">
          <Image src="/logo.svg" alt="Mindtale" width={108} height={32} className="h-7 w-auto" priority />
        </Link>

        <nav className="hidden items-center gap-1 md:flex">
          {MENU_LINKS.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              onClick={() =>
                trackEvent("webinar_lp_menu_click", {
                  destination: item.href,
                  location: "desktop_inline",
                })
              }
              className="inline-flex h-9 items-center rounded-full px-3 text-[14px] font-medium text-[#264653]/75 transition hover:bg-[#264653]/5 hover:text-[#264653]"
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Link
            href={WEBINAR.burnoutTestUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() =>
              trackEvent("webinar_burnout_test_click", { location: "header" })
            }
            className="hidden h-9 items-center gap-1.5 rounded-full border border-[#2C969B]/30 px-3.5 text-[13px] font-semibold text-[#0F6B70] transition hover:bg-[#2C969B]/8 sm:inline-flex"
          >
            Test burnout
          </Link>
          <button
            type="button"
            onClick={() => scrollToForm("header")}
            className="hidden h-9 items-center gap-1.5 rounded-full bg-[#2C969B] px-4 text-[13px] font-semibold text-white shadow-[0_4px_14px_rgba(44,150,155,0.28)] transition hover:bg-[#22797e] sm:inline-flex"
          >
            Rezervă loc
            <ArrowRight className="size-3.5" />
          </button>
          <button
            type="button"
            aria-label={open ? "Închide meniul" : "Deschide meniul"}
            aria-expanded={open}
            onClick={() => {
              if (!open) trackEvent("webinar_lp_menu_open", { location: "header" });
              setOpen((p) => !p);
            }}
            className="inline-flex size-9 items-center justify-center rounded-full text-[#264653]/70 transition hover:bg-[#264653]/5 hover:text-[#264653] md:hidden"
          >
            {open ? <X className="size-5" /> : <Menu className="size-5" />}
          </button>
        </div>
      </div>

      {open && (
        <>
          <button
            type="button"
            aria-label="Închide meniul"
            onClick={() => setOpen(false)}
            className="fixed inset-0 top-14 z-30 bg-[#264653]/30 backdrop-blur-[2px]"
          />
          <div className="absolute inset-x-3 top-full z-40 mx-auto mt-2 max-w-md rounded-2xl border border-[#264653]/8 bg-white shadow-[0_24px_60px_rgba(38,70,83,0.25)]">
            <nav className="flex flex-col px-2 py-2">
              {MENU_LINKS.map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  onClick={() => {
                    trackEvent("webinar_lp_menu_click", { destination: item.href });
                    setOpen(false);
                  }}
                  className="flex items-center justify-between rounded-lg px-3 py-2.5 text-[14px] font-medium text-[#264653] transition hover:bg-[#FFF9F5]"
                >
                  {item.label}
                  <ArrowRight className="size-3.5 text-[#264653]/40" />
                </Link>
              ))}
              <Link
                href={WEBINAR.burnoutTestUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  trackEvent("webinar_burnout_test_click", { location: "menu" });
                  setOpen(false);
                }}
                className="mt-1 flex items-center justify-between rounded-lg border border-[#2C969B]/25 px-3 py-2.5 text-[14px] font-semibold text-[#0F6B70] transition hover:bg-[#2C969B]/8"
              >
                Fă testul de burnout (gratuit)
                <ArrowUpRight className="size-3.5 text-[#0F6B70]/60" />
              </Link>
            </nav>
          </div>
        </>
      )}
    </header>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Hero — Mindtale's signature white card on cream, with the brand gradient
// hairline + soft corner glows. Left: copy + calm date line + CTA. Right:
// horizontal 16:9 speaker video, softly framed (no neon / event-funnel chrome).
// ──────────────────────────────────────────────────────────────────────────
function Hero() {
  return (
    <section className="bg-white">
      <div className="mx-auto max-w-6xl px-4 pb-12 pt-10 sm:px-6 sm:pb-16 sm:pt-16">
        <div className="grid items-center gap-10 lg:grid-cols-[0.95fr_1.1fr] lg:gap-14">
          {/* Text column */}
          <div className="flex flex-col items-center text-center lg:items-start lg:text-left">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-[#2C969B]/25 bg-white/80 px-3 py-1.5 text-[12px] font-medium text-[#264653]/85 backdrop-blur">
              <Sparkles className="size-3.5 text-[#2C969B]" />
              Webinar gratuit ·{" "}
              <span className="font-semibold text-[#2C969B]">{WEBINAR.durationLabel}</span>
            </span>

            <h1 className="mt-5 max-w-xl text-[clamp(1.5rem,5.5vw,1.875rem)] font-bold leading-[1.1] tracking-tight text-[#264653] sm:text-[2.5rem] lg:text-[2.75rem]">
              Te simți epuizat,{" "}
              <span className="text-[#2C969B]">oricât te-ai odihni?</span>
            </h1>

            <p className="mt-5 max-w-lg text-[15px] leading-relaxed text-[#264653]/75 sm:text-lg">
              Un webinar gratuit despre burnout: ce este de fapt, cum îl
              recunoști din timp și ce poți face. Explicat simplu, de un
              psihoterapeut acreditat COPSI.
            </p>

            {/* Calm date line — human, not a ticking clock */}
            <div className="mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-[14px] text-[#264653]/75 lg:justify-start">
              <span className="inline-flex items-center gap-1.5 font-semibold text-[#264653]">
                <Calendar className="size-4 text-[#2C969B]" />
                {WEBINAR.dateLabel}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Clock className="size-4 text-[#2C969B]" />
                {WEBINAR.timeLabel} (ora României)
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Video className="size-4 text-[#2C969B]" />
                {WEBINAR.format}
              </span>
            </div>

            <UrgencyLine />

            {/* Mini "what you'll get" — gives the left column substance next to
                the video so it doesn't float. */}
            <ul className="mt-6 space-y-2 text-left">
              {[
                "Recunoști semnele din timp",
                "Înțelegi ce ți se întâmplă, fără jargon",
                "Pleci cu pași practici",
              ].map((item) => (
                <li
                  key={item}
                  className="flex items-center gap-2.5 text-[14px] text-[#264653]/80 sm:text-[15px]"
                >
                  <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-[#2C969B]/12">
                    <Check className="size-3 text-[#0F6B70]" />
                  </span>
                  {item}
                </li>
              ))}
            </ul>

            {/* Primary CTA (reserve) + secondary CTA (scroll to what you'll learn) */}
            <div className="mt-7 flex flex-col items-center gap-2.5 sm:flex-row sm:gap-3 lg:items-start">
              <button
                type="button"
                onClick={() => scrollToForm("hero")}
                className="inline-flex h-12 items-center gap-2 rounded-full bg-[#2C969B] px-7 text-[15px] font-semibold text-white shadow-[0_8px_22px_rgba(44,150,155,0.32)] transition hover:bg-[#22797e] hover:shadow-[0_10px_26px_rgba(44,150,155,0.42)]"
              >
                Rezervă-ți locul
                <ArrowRight className="size-4" />
              </button>
              <Link
                href={WEBINAR.burnoutTestUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() =>
                  trackEvent("webinar_burnout_test_click", { location: "hero" })
                }
                className="inline-flex h-12 items-center gap-1.5 rounded-full border border-[#264653]/15 bg-white px-6 text-[15px] font-semibold text-[#264653] transition hover:border-[#2C969B]/40 hover:text-[#0F6B70]"
              >
                Fă testul de burnout
                <ArrowUpRight className="size-4" />
              </Link>
            </div>

            <p className="mt-3 inline-flex items-center gap-1.5 text-[12px] text-[#264653]/55">
              <Check className="size-3.5 text-[#0F6B70]" />
              Fără card · primești linkul de acces pe email
            </p>

            <SocialProof className="mt-4" />
          </div>

          {/* Speaker promo image — the visual hero */}
          <Reveal delay={0.1}>
            <HeroImage />
          </Reveal>
        </div>
      </div>
    </section>
  );
}

// Speaker promo image (9:16). The "Webinar GRATUIT" artwork carries the message;
// the CSS frame supplies the rounded corners.
function HeroImage() {
  return (
    <div className="relative mx-auto w-full max-w-[300px] sm:max-w-[340px]">
      <div className="relative aspect-[9/16] overflow-hidden rounded-3xl shadow-[0_30px_70px_rgba(38,70,83,0.18)] ring-1 ring-[#264653]/8 sm:rounded-[2rem]">
        <Image
          src="/poza-webinar.jpg"
          alt="Webinar gratuit despre burnout cu Paula Pricope"
          fill
          priority
          className="object-cover"
          sizes="(max-width: 640px) 300px, 340px"
        />
      </div>

      {/* Soft speaker label */}
      <div className="absolute -bottom-4 left-1/2 inline-flex -translate-x-1/2 items-center gap-2 whitespace-nowrap rounded-full bg-white px-3 py-1.5 text-[12px] font-medium text-[#264653] shadow-[0_6px_18px_rgba(38,70,83,0.18)] ring-1 ring-[#264653]/8">
        <span className="size-1.5 rounded-full bg-[#DC7050]" />
        {WEBINAR.speakerName} · psihoterapeut COPSI
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Trust band — factual credibility strip (no outcome claims, COPSI rules).
// ──────────────────────────────────────────────────────────────────────────
function TrustBand() {
  const items = [
    { icon: BadgeCheck, label: "Susținut de psihoterapeut COPSI" },
    { icon: Video, label: WEBINAR.format },
    { icon: Shield, label: "Confidențial · fără înregistrare publică" },
  ];
  return (
    <section className="border-y border-[#264653]/8 bg-[#FAFAFA]">
      <div className="mx-auto max-w-5xl px-4 py-6 sm:px-6 sm:py-7">
        {/* Relevance anchor — legitimizes the topic and signals "nu ești singur". */}
        <p className="text-center text-[15px] leading-relaxed text-[#264653] sm:text-[16px]">
          <span className="font-bold text-[#2C969B]">67%</span> dintre angajații
          din România resimt simptome de burnout.
        </p>
        <ul className="mt-4 flex flex-wrap items-center justify-center gap-x-6 gap-y-2.5 text-[12px] font-medium text-[#264653]/75 sm:text-[13px]">
          {items.map((it) => (
            <li key={it.label} className="inline-flex items-center gap-1.5">
              <it.icon className="size-4 text-[#0F6B70]" />
              {it.label}
            </li>
          ))}
          <li className="inline-flex items-center gap-1.5">
            <span className="inline-block size-1.5 rounded-full bg-[#DC7050]" />
            100% în română
          </li>
        </ul>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Empathy / self-identification — mirrors the reader's pain in their own words
// so they think "asta sunt eu". Warmest, most persuasive block on the page.
// Soft burnt-orange wash ties in the brand's secondary color.
// ──────────────────────────────────────────────────────────────────────────
function EmpathySection() {
  const signs = [
    "Te trezești obosit, deși ai dormit.",
    "Lucruri care înainte îți plăceau acum par o corvoadă.",
    "Ești mai irascibil cu cei dragi și nu știi de ce.",
    "Dai tot ce ai, dar parcă nu mai ajunge niciodată.",
    "Te gândești „poate sunt doar leneș” și te simți vinovat.",
    "Trece weekendul și luni te simți la fel de gol.",
  ];
  return (
    <section className="border-y border-[#DC7050]/10 bg-[#FDF6F2]">
      <div className="mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-20">
        <p className="text-center text-[10px] font-bold uppercase tracking-[0.18em] text-[#C9542F]">
          Te recunoști?
        </p>
        <h2 className="mx-auto mt-3 max-w-2xl text-center text-2xl font-semibold tracking-tight text-[#264653] sm:text-3xl">
          Poate ți se pare cunoscut.
        </h2>

        <div className="mt-10 grid items-start gap-8 lg:grid-cols-[260px_1fr] lg:gap-12">
          {/* Calm portrait — soft human presence next to the signs */}
          <div className="mx-auto w-full max-w-[220px] sm:max-w-[250px] lg:max-w-none">
            <div className="relative aspect-[9/16] overflow-hidden rounded-2xl ring-1 ring-[#264653]/10 shadow-[0_16px_44px_rgba(38,70,83,0.16)]">
              <Image
                src="/paula-2.jpg"
                alt={`${WEBINAR.speakerFullName}, psihoterapeut acreditat COPSI`}
                fill
                className="object-cover"
                sizes="(max-width: 1024px) 250px, 260px"
              />
            </div>
          </div>

          {/* Signs + reassurance */}
          <div>
            <div className="grid gap-3 sm:grid-cols-2">
              {signs.map((s) => (
                <div
                  key={s}
                  className="flex items-start gap-3 rounded-2xl border border-[#DC7050]/15 bg-white p-4 shadow-[0_2px_16px_rgba(220,112,80,0.05)]"
                >
                  <span className="mt-[7px] size-2 shrink-0 rounded-full bg-[#DC7050]" />
                  <p className="text-[14px] leading-relaxed text-[#264653]/80">{s}</p>
                </div>
              ))}
            </div>

            <div className="mt-6 rounded-2xl bg-[#264653] p-5 text-center sm:p-6">
              <p className="text-[15px] leading-relaxed text-white sm:text-[16px]">
                Dacă ai dat din cap la măcar unul, webinarul ăsta e pentru tine.{" "}
                <span className="font-semibold text-[#FECA2E]">
                  Nu e lipsă de voință. E epuizare
                </span>
                , și se poate schimba.
              </p>
            </div>

            <div className="mt-4 text-center">
              <Link
                href={WEBINAR.burnoutTestUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() =>
                  trackEvent("webinar_burnout_test_click", { webinar: "burnout" })
                }
                className="inline-flex items-center gap-1.5 text-[14px] font-semibold text-[#C9542F] transition hover:underline"
              >
                Nu ești sigur? Fă testul de burnout (2 min, gratuit)
                <ArrowRight className="size-4" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// What you'll learn — 4 benefit cards. Reframes burnout away from "lazy/weak".
// ──────────────────────────────────────────────────────────────────────────
function WhatYouLearn() {
  const points = [
    {
      tag: "Semnele",
      title: "Cum recunoști burnout-ul din timp",
      body: "Semnele pe care le confundăm cu oboseala normală și linia subțire dintre „sunt obosit” și „sunt epuizat”.",
    },
    {
      tag: "Mitul",
      title: "De ce nu e lene sau lipsă de voință",
      body: "Ce se întâmplă de fapt în corp și minte la epuizare, ca să nu te mai învinovățești pentru ceva ce nu e vina ta.",
      accent: true,
    },
    {
      tag: "Practica",
      title: "Pași concreți pentru azi",
      body: "Câteva lucruri pe care le poți face în săptămâna asta ca să-ți scazi încărcătura, fără să-ți dai demisia.",
    },
    {
      tag: "Sprijinul",
      title: "Când și cum ceri ajutor",
      body: "Cum arată sprijinul potrivit și ce poți aștepta de la terapie, dacă simți că ai nevoie de mai mult.",
    },
  ];

  return (
    <section id="ce-inveti" className="bg-white">
      <div className="mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-20">
        <p className="text-center text-[10px] font-bold uppercase tracking-[0.18em] text-[#2C969B]">
          Ce vei lua cu tine
        </p>
        <h2 className="mx-auto mt-3 max-w-2xl text-center text-2xl font-semibold tracking-tight text-[#264653] sm:text-3xl">
          Patru lucruri pe care le iei cu tine acasă.
        </h2>

        <div className="mt-10 grid gap-4 sm:grid-cols-2">
          {points.map((p) => (
            <div
              key={p.title}
              className={
                p.accent
                  ? "rounded-3xl border border-[#DC7050]/30 bg-gradient-to-br from-[#DC7050]/10 to-white p-6 shadow-[0_4px_24px_rgba(220,112,80,0.08)] transition hover:-translate-y-0.5 hover:shadow-[0_12px_36px_rgba(220,112,80,0.16)]"
                  : "rounded-3xl border border-[#264653]/10 bg-white p-6 shadow-[0_4px_24px_rgba(38,70,83,0.04)] transition hover:-translate-y-0.5 hover:shadow-[0_12px_36px_rgba(44,150,155,0.12)]"
              }
            >
              <span
                className={
                  p.accent
                    ? "inline-flex items-center gap-1.5 rounded-full bg-[#DC7050]/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#C9542F]"
                    : "inline-flex items-center gap-1.5 rounded-full bg-[#FECA2E]/25 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#A33A1F]"
                }
              >
                {p.tag}
              </span>
              <h3 className="mt-3 text-[17px] font-semibold text-[#264653]">
                {p.title}
              </h3>
              <p className="mt-1.5 text-[14px] leading-relaxed text-[#264653]/70">
                {p.body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Mid-page CTA — keeps a primary action in view on desktop between the hero
// and the form (6 sections apart otherwise).
// ──────────────────────────────────────────────────────────────────────────
function MidCTA() {
  return (
    <section className="bg-[#FAFAFA]">
      <div className="mx-auto max-w-3xl px-4 py-12 text-center sm:px-6 sm:py-14">
        <h2 className="text-xl font-semibold tracking-tight text-[#264653] sm:text-2xl">
          Te regăsești în ce ai citit?
        </h2>
        <p className="mx-auto mt-2 max-w-md text-[14px] text-[#264653]/65 sm:text-[15px]">
          Rezervă-ți locul acum. E gratuit, online și fără card.
        </p>
        <button
          type="button"
          onClick={() => scrollToForm("mid")}
          className="mt-5 inline-flex h-12 items-center gap-2 rounded-full bg-[#2C969B] px-6 text-[15px] font-semibold text-white shadow-[0_8px_22px_rgba(44,150,155,0.32)] transition hover:bg-[#22797e]"
        >
          Rezervă-ți locul
          <ArrowRight className="size-4" />
        </button>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Agenda — simple timeline of the hour. Makes the value concrete.
// ──────────────────────────────────────────────────────────────────────────
function Agenda() {
  const blocks = [
    { title: "Ce este burnout-ul și cum apare" },
    { title: "Factorii de risc" },
    { title: "Vulnerabilitatea la stres" },
    { title: "Exercițiu: „Stress Bucket”", note: "Un exercițiu practic, pe care îl faci live." },
    { title: "Impactul burnout-ului" },
    { title: "Construirea rezilienței", note: "Strategii practice de prevenire și gestionare." },
    { title: "Roadmap-ul burnout-ului" },
    { title: "Obiceiuri care susțin recuperarea" },
    { title: "Întrebări și răspunsuri (Q&A)", note: "Întrebi live, anonim dacă vrei." },
  ];

  return (
    <section className="border-y border-[#264653]/8 bg-[#FAFAFA]">
      <div className="mx-auto max-w-3xl px-4 py-14 sm:px-6 sm:py-20">
        <p className="text-center text-[10px] font-bold uppercase tracking-[0.18em] text-[#2C969B]">
          Programul webinarului
        </p>
        <h2 className="mt-3 text-center text-2xl font-semibold tracking-tight text-[#264653] sm:text-3xl">
          Ce parcurgem împreună
        </h2>
        <p className="mx-auto mt-3 max-w-lg text-center text-[14px] text-[#264653]/65 sm:text-[15px]">
          {WEBINAR.durationLabel}, {WEBINAR.format.toLowerCase()}. Vii cu o cafea,
          pleci cu claritate.
        </p>

        <ol className="mx-auto mt-10 grid max-w-3xl gap-3.5 sm:grid-cols-2">
          {blocks.map((b, i) => {
            const isLast = i === blocks.length - 1;
            return (
              <li
                key={b.title}
                className={`flex items-start gap-3.5 rounded-2xl border p-4 shadow-[0_2px_14px_rgba(38,70,83,0.04)] ${
                  isLast
                    ? "border-[#2C969B]/25 bg-[#2C969B]/[0.06] sm:col-span-2"
                    : "border-[#264653]/10 bg-white"
                }`}
              >
                <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-[#2C969B]/12 text-[13px] font-bold text-[#0F6B70]">
                  {i + 1}
                </span>
                <div>
                  <h3 className="text-[15px] font-semibold text-[#264653]">
                    {b.title}
                  </h3>
                  {b.note && (
                    <p className="mt-1 text-[13px] leading-relaxed text-[#264653]/65">
                      {b.note}
                    </p>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Speaker — the face that runs the webinar. Trust anchor. Photo + credentials
// + one authentic quote (no fabricated testimonials, COPSI rules).
// ──────────────────────────────────────────────────────────────────────────
function Speaker() {
  const credentials = [
    `${WEBINAR.speakerYears} ani de practică`,
    "Acreditat COPSI",
    `Focus: ${WEBINAR.speakerFocus}`,
  ];
  return (
    <section className="bg-white">
      <div className="mx-auto max-w-4xl px-4 py-14 sm:px-6 sm:py-20">
        <p className="text-center text-[10px] font-bold uppercase tracking-[0.18em] text-[#2C969B]">
          Cine susține webinarul
        </p>
        <h2 className="mx-auto mt-3 max-w-xl text-center text-2xl font-semibold tracking-tight text-[#264653] sm:text-3xl">
          Înveți de la cineva care face asta zi de zi.
        </h2>

        <div className="mt-10 grid items-center gap-8 rounded-3xl border border-[#2C969B]/15 bg-gradient-to-br from-[#2C969B]/8 via-white to-[#FECA2E]/12 p-6 shadow-[0_8px_28px_rgba(38,70,83,0.06)] sm:p-8 lg:grid-cols-[300px_1fr] lg:gap-10 lg:p-10">
          {/* Portrait — transparent cutout on a burnt-orange panel. Capped on
              mobile so it doesn't fill the screen. */}
          <div className="mx-auto w-full max-w-[230px] sm:max-w-[260px] lg:max-w-none">
            <div className="relative aspect-[4/5] overflow-hidden rounded-2xl bg-gradient-to-br from-[#DC7050] to-[#C9542F] shadow-[0_16px_44px_rgba(220,112,80,0.25)]">
              <Image
                src="/founder.png"
                alt={`${WEBINAR.speakerFullName}, ${WEBINAR.speakerRole}`}
                fill
                className="object-cover object-top"
                sizes="(max-width: 1024px) 260px, 300px"
              />
            </div>
          </div>

          {/* Text */}
          <div className="relative text-center lg:text-left">
            <Quote className="pointer-events-none absolute -top-3 right-0 hidden size-20 text-[#2C969B]/10 lg:block" aria-hidden />
            <p className="text-[18px] font-semibold text-[#264653]">
              {WEBINAR.speakerFullName}
            </p>
            <p className="mt-0.5 text-[12px] text-[#264653]/60">
              {WEBINAR.speakerRole}
            </p>

            <div className="mt-3 flex flex-wrap justify-center gap-1.5 lg:justify-start">
              {credentials.map((c) => (
                <span
                  key={c}
                  className="inline-flex items-center gap-1 rounded-full bg-white px-2.5 py-1 text-[11px] font-medium text-[#264653]/80 ring-1 ring-[#264653]/10"
                >
                  <BadgeCheck className="size-3 text-[#0F6B70]" />
                  {c}
                </span>
              ))}
            </div>

            <blockquote className="mt-4 text-[16px] font-medium leading-relaxed text-[#264653] sm:text-[19px] sm:leading-[1.5]">
              „Cei mai mulți oameni care ajung la mine cu burnout cred că pur și
              simplu nu se străduiesc destul. Aproape niciodată nu e adevărat.
              Vreau ca ora asta să-ți dea limbajul și sprijinul să te înțelegi
              altfel.”
            </blockquote>

            <a
              href={WEBINAR.speakerProfileUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() =>
                trackEvent("webinar_speaker_profile_click", { webinar: "burnout" })
              }
              className="mt-5 inline-flex items-center gap-1.5 text-[14px] font-semibold text-[#0F6B70] transition hover:text-[#22797e]"
            >
              Vezi profilul complet
              <ArrowUpRight className="size-4" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Registration — the conversion point, in the site's warm skin (white card on
// cream). PLACEHOLDER submit: validates client-side, fires analytics, shows a
// success state. Wire the real submit (Brevo /api/newsletter, or Resend) at TODO.
// ──────────────────────────────────────────────────────────────────────────
function Registration() {
  return (
    <section id="inscriere" className="scroll-mt-20 bg-white">
      <div className="mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-20">
        <div className="grid items-center gap-8 lg:grid-cols-[280px_1fr] lg:gap-12">
          {/* Promo image — reinforces "gratis" right at the decision point */}
          <div className="mx-auto w-full max-w-[230px] sm:max-w-[260px] lg:max-w-none">
            <div className="relative aspect-[9/16] overflow-hidden rounded-2xl ring-1 ring-[#DC7050]/15 shadow-[0_16px_44px_rgba(220,112,80,0.18)]">
              <Image
                src="/paulagratis.jpg"
                alt="Webinar gratuit despre burnout cu Paula Pricope"
                fill
                className="object-cover"
                sizes="(max-width: 1024px) 260px, 280px"
              />
            </div>
          </div>

          {/* Heading + form */}
          <div>
            <div className="mx-auto max-w-md text-center">
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#2C969B]">
                Înscriere
              </p>
              <h2 className="mt-3 text-2xl font-semibold tracking-tight text-[#264653] sm:text-3xl">
                Rezervă-ți locul gratuit
              </h2>
              <p className="mx-auto mt-3 max-w-sm text-[14px] text-[#264653]/65 sm:text-[15px]">
                Lasă-ne emailul. Îți trimitem linkul de acces și un reminder
                înainte de webinar.
              </p>
              <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[#DC7050]/10 px-3 py-1 text-[12px] font-semibold text-[#C9542F]">
                  <Sparkles className="size-3.5" />
                  Locuri limitate
                </span>
                <SocialProof />
              </div>
            </div>

            <WebinarForm location="section" />
          </div>
        </div>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// FAQ — objection handling + FAQPage JSON-LD for rich results.
// ──────────────────────────────────────────────────────────────────────────
const FAQ_ITEMS = [
  {
    q: "E gratuit cu adevărat?",
    a: "Da, de la cap la coadă. Nu cerem card și nu apare nicio plată mai târziu. Te înscrii, primești linkul pe email, participi.",
  },
  {
    q: "Pentru cine e webinarul?",
    a: "Pentru tine, dacă te simți epuizat, suprasolicitat sau aproape de limită, fie din cauza muncii, fie a vieții de zi cu zi. Nu trebuie să fii „într-un caz grav” ca să vii și nu ai nevoie de nicio pregătire.",
  },
  {
    q: "Trebuie să apar pe cameră sau să vorbesc?",
    a: "Nu. Stai cu camera și microfonul închise cât vrei. Întrebările le poți scrie, anonim dacă preferi. Nimeni nu te pune pe loc.",
  },
  {
    q: "Se înregistrează webinarul?",
    a: "Nu, e doar live. Așa discuțiile și întrebările rămân între noi. Blochează-ți ora în calendar ca să nu o ratezi.",
  },
  {
    q: "De ce îmi cereți numărul de telefon?",
    a: "Ca să-ți trimitem un reminder scurt înainte de webinar, fiindcă multe linkuri se pierd în inbox. Datele tale sunt protejate conform GDPR și nu le dăm nimănui.",
  },
  {
    q: "E o ședință de terapie?",
    a: "Nu, e un webinar educativ, de grup. Nu înlocuiește terapia, dar te ajută să-ți dai seama dacă și când ți-ar prinde bine un psihoterapeut.",
  },
];

function FAQ() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ_ITEMS.map((it) => ({
      "@type": "Question",
      name: it.q,
      acceptedAnswer: { "@type": "Answer", text: it.a },
    })),
  };

  return (
    <section className="border-t border-[#264653]/8 bg-[#FAFAFA]">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
      />
      <div className="mx-auto max-w-3xl px-4 py-14 sm:px-6 sm:py-20">
        <p className="text-center text-[10px] font-bold uppercase tracking-[0.18em] text-[#2C969B]">
          Întrebări frecvente
        </p>
        <h2 className="mt-3 text-center text-2xl font-semibold tracking-tight text-[#264653] sm:text-3xl">
          Ce te-ai putea întreba
        </h2>

        <div className="mt-8 space-y-2">
          {FAQ_ITEMS.map((item, i) => (
            <FAQItem key={i} q={item.q} a={item.a} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQItem({ q, a, index }: { q: string; a: string; index: number }) {
  const [open, setOpen] = useState(false);
  return (
    <details
      className="group rounded-xl border border-[#264653]/8 bg-white p-4 transition open:border-[#2C969B]/30 open:shadow-[0_4px_16px_rgba(44,150,155,0.06)]"
      onToggle={(e) => {
        const isOpen = (e.target as HTMLDetailsElement).open;
        setOpen(isOpen);
        if (isOpen) trackEvent("webinar_lp_faq_open", { index, question: q });
      }}
    >
      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 text-[15px] font-semibold text-[#264653] [&::-webkit-details-marker]:hidden">
        {q}
        <ChevronRight
          className={`size-4 shrink-0 text-[#2C969B] transition-transform ${
            open ? "rotate-90" : ""
          }`}
        />
      </summary>
      <p className="mt-3 text-[14px] leading-relaxed text-[#264653]/75">{a}</p>
    </details>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Final CTA — restate the event + single action back to the form.
// ──────────────────────────────────────────────────────────────────────────
function FinalCTA() {
  return (
    <section className="bg-gradient-to-br from-[#DC7050] to-[#C9542F] text-white">
      <div className="mx-auto max-w-3xl px-4 py-16 text-center sm:px-6 sm:py-20">
        <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">
          O oră și jumătate acum te poate scuti de luni de epuizare.
        </h2>
        <p className="mx-auto mt-3 max-w-xl text-[14px] text-white/85 sm:text-[15px]">
          {WEBINAR.dateLabel}, {WEBINAR.timeLabel}. {WEBINAR.format}. Gratuit,
          fără card.
        </p>
        <button
          type="button"
          onClick={() => scrollToForm("final")}
          className="mt-7 inline-flex h-12 items-center gap-2 rounded-full bg-white px-6 text-[15px] font-semibold text-[#C9542F] shadow-[0_8px_22px_rgba(0,0,0,0.18)] transition hover:bg-[#FFF9F5]"
        >
          Rezervă-ți locul gratuit
          <ArrowRight className="size-4" />
        </button>
        <ul className="mx-auto mt-6 flex max-w-md flex-wrap items-center justify-center gap-x-4 gap-y-2 text-[12px] text-white/80">
          <li className="inline-flex items-center gap-1">
            <Check className="size-3.5" /> Gratuit
          </li>
          <li className="inline-flex items-center gap-1">
            <Check className="size-3.5" /> Fără card
          </li>
          <li className="inline-flex items-center gap-1">
            <Check className="size-3.5" /> Întrebări live
          </li>
          <li className="inline-flex items-center gap-1">
            <Check className="size-3.5" /> Psihoterapeut COPSI
          </li>
        </ul>
      </div>
    </section>
  );
}

function MinimalFooter() {
  return (
    <footer className="border-t border-[#264653]/8 bg-white">
      <div className="mx-auto flex max-w-5xl flex-col items-center gap-3 px-4 py-8 text-center text-[12px] text-[#264653]/55 sm:flex-row sm:justify-between sm:gap-0 sm:px-6 sm:text-left">
        <p>
          © {new Date().getFullYear()} Mindtale, marcă a Zenyra Therapy SRL.
          Terapeuții sunt acreditați COPSI.
        </p>
        <div className="flex items-center gap-4">
          <Link href="/termeni" className="hover:text-[#264653]">
            Termeni
          </Link>
          <Link href="/confidentialitate" className="hover:text-[#264653]">
            Confidențialitate
          </Link>
          <Link href="/contact" className="hover:text-[#264653]">
            Contact
          </Link>
        </div>
      </div>
    </footer>
  );
}

function StickyMobileCTA() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 600);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (!visible) return null;

  return (
    <div className="fixed inset-x-3 bottom-3 z-30 sm:hidden">
      <button
        type="button"
        onClick={() => scrollToForm("sticky_mobile")}
        className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-[#2C969B] text-[15px] font-semibold text-white shadow-[0_10px_30px_rgba(44,150,155,0.45)]"
      >
        Rezervă-ți locul gratuit
        <ArrowRight className="size-4" />
      </button>
    </div>
  );
}


// ──────────────────────────────────────────────────────────────────────────
// Closed state — shown once the event date has passed. One job: send the
// visitor to the next webinar, or to something useful about burnout.
// ──────────────────────────────────────────────────────────────────────────
function EndedPage() {
  const next = [
    {
      href: "/teste/burnout",
      eyebrow: "5 minute",
      title: "Testul de burnout",
      body: "Vezi unde te afli acum, pe baza unui chestionar validat clinic. Gratuit și fără cont.",
      event: "webinar_ended_test",
    },
    {
      href: "/burnout",
      eyebrow: "Ghid",
      title: "Tot ce trebuie să știi despre burnout",
      body: "Cum îl recunoști, ce nu este și ce poți face. Scris de psihoterapeuți acreditați.",
      event: "webinar_ended_topic",
    },
  ];

  return (
    <div className="min-h-screen bg-[#FFF9F5]">
      <header className="border-b border-[#264653]/10">
        <div className="mx-auto flex h-16 max-w-[1240px] items-center justify-between px-5 sm:h-[4.5rem] sm:px-8">
          <Link href="/" aria-label="Mindtale" className="flex items-center">
            <Image src="/logo.svg" alt="Mindtale" width={108} height={32} className="h-7 w-auto sm:h-8" priority />
          </Link>
          <Link
            href="/terapeuti"
            className="text-[14px] font-medium text-[#264653]/70 transition-colors hover:text-[#264653]"
          >
            Vezi terapeuții
          </Link>
        </div>
      </header>

      <section className="px-5 sm:px-8">
        <div className="mx-auto max-w-[900px] pb-16 pt-20 text-center sm:pb-20 sm:pt-28">
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#264653]/45">
            Eveniment încheiat
          </p>
          <h1 className="mx-auto mt-6 max-w-[22ch] font-serif text-[clamp(2rem,4.6vw,3.5rem)] font-normal leading-[1.08] tracking-[-0.015em] text-[#264653]">
            Webinarul despre burnout s-a încheiat.
          </h1>
          <p className="mx-auto mt-7 max-w-[46rem] text-pretty text-[16px] leading-relaxed text-[#264653]/70 sm:text-[17px]">
            A avut loc pe 9 iulie și nu a fost înregistrat, ca întrebările
            participanților să rămână între ei. Mai jos găsești unde să începi
            dacă vrei să afli mai multe despre burnout.
          </p>
        </div>
      </section>

      <section className="border-t border-[#264653]/10 px-5 pb-20 sm:px-8 sm:pb-28">
        <ol className="mx-auto max-w-[820px]">
          {next.map((item, i) => (
            <li key={item.href} className="border-b border-[#264653]/10">
              <Link
                href={item.href}
                onClick={() => trackEvent(item.event, { from: "webinar_burnout_ended" })}
                className="group flex items-baseline gap-5 py-7"
              >
                <span className="w-6 shrink-0 font-serif text-[15px] tabular-nums text-[#DC7050]">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="flex-1">
                  <span className="block text-[11px] font-semibold uppercase tracking-[0.18em] text-[#2C969B]">
                    {item.eyebrow}
                  </span>
                  <span className="mt-2 block font-serif text-[19px] font-medium leading-snug text-[#264653] sm:text-[22px]">
                    {item.title}
                  </span>
                  <span className="mt-2 block text-[15px] leading-relaxed text-[#264653]/70 sm:text-[16px]">
                    {item.body}
                  </span>
                </span>
                <ArrowRight className="mt-1 size-4 shrink-0 text-[#264653]/35 transition-transform group-hover:translate-x-0.5" />
              </Link>
            </li>
          ))}
        </ol>
      </section>

      <footer className="border-t border-[#264653]/10 px-5 py-10 sm:px-8">
        <div className="mx-auto flex max-w-[1240px] flex-col items-center gap-3 text-center text-[12px] text-[#264653]/55 sm:flex-row sm:justify-between sm:gap-0 sm:text-left">
          <p>
            © {new Date().getFullYear()} Mindtale, marcă a Zenyra Therapy SRL.
            Terapeuții sunt acreditați COPSI.
          </p>
          <div className="flex items-center gap-5">
            <Link href="/termeni" className="transition-colors hover:text-[#264653]">
              Termeni
            </Link>
            <Link href="/confidentialitate" className="transition-colors hover:text-[#264653]">
              Confidențialitate
            </Link>
            <Link href="/contact" className="transition-colors hover:text-[#264653]">
              Contact
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
